import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { PixelIcon } from "./PixelIcon";

export interface SheetOverlayProps {
  testId?: string;
  title: string;
  label?: string;
  onClose(): void;
  /** Escape asks this first: return true when it closed an inner view (a confirmation, a form mode) so the sheet stays open. */
  onEscape?(): boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
}

export function SheetOverlay({
  testId,
  title,
  label,
  onClose,
  onEscape,
  initialFocusRef,
  children,
}: SheetOverlayProps) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  });

  useEffect(() => {
    if (initialFocusRef?.current) {
      initialFocusRef.current.focus();
    } else {
      closeRef.current?.focus();
    }
  }, [initialFocusRef]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (onEscapeRef.current?.()) return;
        onClose();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <>
      <div
        className="sheet-backdrop"
        data-testid="sheet-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="sheet bottom"
        id="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={label ?? title}
        data-testid={testId}
      >
        <div className="sheet-handle" aria-hidden="true" />
        <div className="sh">
          <h3>{title}</h3>
          <button
            ref={closeRef}
            className="x"
            type="button"
            aria-label="Close"
            data-testid="sheet-close"
            onClick={onClose}
          >
            <PixelIcon name="close" />
          </button>
        </div>
        {children}
      </div>
    </>
  );
}
